import React from 'react';
import { Table, flexRender } from '@tanstack/react-table';
import { TanStackPaginationControls } from '@/components/shared/TanStackPaginationControls';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

export interface TanStackTableViewProps<T = any> {
    table: Table<T>;
    isLoading?: boolean;
    loadingMessage?: string;
    className?: string;
    showPaginationOnly?: boolean;
    cellVerticalAlign?: 'top' | 'middle';
}

const TanStackTableView = <T,>({
    table,
    isLoading = false,
    loadingMessage = 'Carregando...',
    className = '',
    showPaginationOnly = false,
    cellVerticalAlign = 'top',
}: TanStackTableViewProps<T>) => {
    if (isLoading) {
        return <div className={`p-4 text-center ${className}`}>{loadingMessage}</div>;
    }

    if (showPaginationOnly) {
        return (
            <div className={`flex flex-col ${className}`}>
                <TanStackPaginationControls table={table} />
            </div>
        );
    }

    return (
        <div className={`flex flex-col ${className}`}>
            <div className="-my-2">
                <div className="py-2 align-middle inline-block min-w-full">
                    <div className="shadow border-b border-gray-200 sm:rounded-lg overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 table-fixed">
                            <thead className="bg-gray-50">
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => (
                                            <th
                                                key={header.id}
                                                scope="col"
                                                className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${header.id === 'actions' ? 'text-right' : ''
                                                    } ${header.column.getCanSort() ? 'cursor-pointer select-none hover:bg-gray-100' : ''
                                                    }`}
                                                onClick={header.column.getToggleSortingHandler()}
                                            >
                                                <div className={`flex items-center ${header.id === 'actions' ? 'justify-end' : 'justify-start'}`}>
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                    {header.column.getCanSort() && (
                                                        <div className="ml-1 flex flex-col">
                                                            {header.column.getIsSorted() === 'asc' ? (
                                                                <ChevronUpIcon className="w-4 h-4 text-gray-600" />
                                                            ) : header.column.getIsSorted() === 'desc' ? (
                                                                <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                                                            ) : (
                                                                <div className="flex flex-col">
                                                                    <ChevronUpIcon className="w-3 h-3 text-gray-400" />
                                                                    <ChevronDownIcon className="w-3 h-3 text-gray-400 -mt-1" />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {table.getRowModel().rows.map(row => (
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map(cell => (
                                            <td
                                                key={cell.id}
                                                className={`px-6 py-4 ${cellVerticalAlign === 'middle' ? 'align-middle' : 'align-top'} ${cell.column.id === 'actions' ? 'text-right text-sm font-medium whitespace-nowrap' : 'break-words whitespace-normal'
                                                    }`}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <TanStackPaginationControls table={table} />
        </div>
    );
};

export default TanStackTableView; 
